import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";
import { createPortal } from "react-dom";
import { LucideIcon } from "../components/FG/LucideIcon";
import { fgPortalMount } from "../lib/fgPortalMount";

export type ToastTone = "info" | "success" | "warning" | "error" | "pending";
export type ToastAction = {
  /** Button label (e.g. "Try again", "Undo"). */
  label: string;
  /** Click handler — receives the toast id so the action can dismiss the toast. */
  onClick: (toastId: string) => void;
};
export type Toast = {
  id: string;
  tone: ToastTone;
  message: string;
  /** Optional secondary line — e.g. "Plan saved to your account · 12 workouts merged" */
  detail?: string;
  /** Optional inline action button (retry, undo, etc.). */
  action?: ToastAction;
};

type ContextValue = {
  toasts: Toast[];
  push: (toast: Omit<Toast, "id"> & { id?: string; durationMs?: number }) => string;
  /**
   * Convenience for the "couldn't do X — try again" pattern. Renders an error
   * toast with a Retry button bound to the supplied handler; the toast
   * dismisses itself when Retry is clicked so the caller doesn't have to.
   */
  pushRetry: (input: {
    message: string;
    detail?: string;
    retry: () => void | Promise<void>;
    retryLabel?: string;
  }) => string;
  /**
   * Show a spinner toast for an in-flight background action (paste, duplicate,
   * apply-plan…). It never auto-dismisses — resolve it with `update(id, …)` to
   * the success/error state when the work lands. Returns the toast id.
   */
  pushPending: (message: string, detail?: string) => string;
  /**
   * Mutate an existing toast in place — used to flip a `pending` toast to its
   * final success/error state. Resets the auto-dismiss timer based on the
   * patched tone/duration (pending stays sticky; everything else dismisses).
   */
  update: (
    id: string,
    patch: Partial<Omit<Toast, "id">> & { durationMs?: number }
  ) => void;
  dismiss: (id: string) => void;
};

const ToastContext = createContext<ContextValue | null>(null);

const DEFAULT_DURATION_MS = 4500;

let counter = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((curr) => curr.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback<ContextValue["push"]>(
    (input) => {
      const id = input.id ?? `t-${++counter}`;
      const duration = input.durationMs ?? DEFAULT_DURATION_MS;
      const toast: Toast = {
        id,
        tone: input.tone,
        message: input.message,
        detail: input.detail,
        action: input.action
      };
      setToasts((curr) => [...curr, toast]);
      if (duration > 0) {
        const timer = setTimeout(() => dismiss(id), duration);
        timers.current.set(id, timer);
      }
      return id;
    },
    [dismiss]
  );

  const pushRetry = useCallback<ContextValue["pushRetry"]>(
    ({ message, detail, retry, retryLabel = "Try again" }) =>
      push({
        tone: "error",
        message,
        detail,
        action: {
          label: retryLabel,
          onClick: (toastId) => {
            dismiss(toastId);
            void Promise.resolve().then(retry);
          }
        }
      }),
    [push, dismiss]
  );

  const pushPending = useCallback<ContextValue["pushPending"]>(
    // durationMs:0 → no auto-dismiss; it lives until `update` or `dismiss`.
    (message, detail) => push({ tone: "pending", message, detail, durationMs: 0 }),
    [push]
  );

  const update = useCallback<ContextValue["update"]>(
    (id, patch) => {
      setToasts((curr) => curr.map((t) => (t.id === id ? { ...t, ...patch } : t)));
      const existing = timers.current.get(id);
      if (existing) {
        clearTimeout(existing);
        timers.current.delete(id);
      }
      // Arm a fresh dismiss timer for the resolved (non-pending) state.
      const duration = patch.durationMs ?? DEFAULT_DURATION_MS;
      if (patch.tone !== "pending" && duration > 0) {
        const timer = setTimeout(() => dismiss(id), duration);
        timers.current.set(id, timer);
      }
    },
    [dismiss]
  );

  const value = useMemo<ContextValue>(
    () => ({ toasts, push, pushRetry, pushPending, update, dismiss }),
    [toasts, push, pushRetry, pushPending, update, dismiss]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastPortal toasts={toasts} dismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastRow({ t, dismiss }: { t: Toast; dismiss: (id: string) => void }) {
  return (
    <div className={`fg-toast fg-toast--${t.tone}`}>
      {t.tone === "pending" && <span className="fg-toast__spinner" aria-hidden />}
      <div className="fg-toast__body">
        <div className="fg-toast__message">{t.message}</div>
        {t.detail && <div className="fg-toast__detail">{t.detail}</div>}
        {t.action && (
          <button
            type="button"
            className="fg-toast__action"
            onClick={() => {
              t.action!.onClick(t.id);
            }}>
            {t.action.label}
          </button>
        )}
      </div>
      <button
        type="button"
        className="fg-toast__close"
        aria-label="Dismiss"
        onClick={() => dismiss(t.id)}>
        <LucideIcon name="x" size={16} />
      </button>
    </div>
  );
}

function ToastPortal({ toasts, dismiss }: { toasts: Toast[]; dismiss: (id: string) => void }) {
  if (typeof document === "undefined") return null;
  const portalRoot = fgPortalMount() ?? document.body;
  // Split into two live regions so error/warning toasts interrupt (assertive)
  // while informational tones stay polite. Both regions reuse `.fg-toasts`
  // styling so the visual stack is unchanged.
  const isAlert = (t: Toast) => t.tone === "error" || t.tone === "warning";
  const statusToasts = toasts.filter((t) => !isAlert(t));
  const alertToasts = toasts.filter(isAlert);
  return createPortal(
    <>
      <div className="fg-toasts" role="status" aria-live="polite">
        {statusToasts.map((t) => (
          <ToastRow key={t.id} t={t} dismiss={dismiss} />
        ))}
      </div>
      <div className="fg-toasts" role="alert" aria-live="assertive">
        {alertToasts.map((t) => (
          <ToastRow key={t.id} t={t} dismiss={dismiss} />
        ))}
      </div>
    </>,
    portalRoot
  );
}

export function useToast(): ContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be called inside <ToastProvider>");
  return ctx;
}
